import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmt, fmtNum } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Sparkles, Save, AlertCircle, CheckCircle2, ArrowLeft, Trash2, Plus, PackagePlus } from "lucide-react";

export default function ImportPO() {
    const nav = useNavigate();
    const [step, setStep] = useState("upload"); // upload | review
    const [file, setFile] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [invoiceType, setInvoiceType] = useState("B2B");
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [delivery, setDelivery] = useState(0);

    const parse = async () => {
        if (!file) return toast.error("Choose a PDF first");
        setParsing(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("invoice_type", invoiceType);
        try {
            const r = await api.post("/invoices/from-po", fd, { timeout: 90000 });
            setDraft(r.data);
            setStep("review");
            toast.success(`Extracted ${r.data.stats.total} items — ${r.data.stats.matched} matched, ${r.data.stats.unmatched} need pricing`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not parse the PO");
        }
        setParsing(false);
    };

    const setLine = (i, patch) => {
        setDraft({ ...draft, line_items: draft.line_items.map((l, k) => k === i ? { ...l, ...patch } : l) });
    };
    const rmLine = (i) => {
        setDraft({ ...draft, line_items: draft.line_items.filter((_, k) => k !== i) });
    };
    const addLine = () => {
        setDraft({ ...draft, line_items: [...draft.line_items, {
            item_id: null, name: "", hsn: "", category: "Uncategorized", unit: "Pack",
            qty: 1, rate: 0, tax_percent: 0, match_status: "unmatched", raw_po_name: "",
        }]});
    };

    const promoteToMaster = async (i) => {
        const l = draft.line_items[i];
        if (!l.name) return toast.error("Item name is required");
        if (!Number(l.rate)) return toast.error("Enter a rate first");
        const isB2C = (draft.invoice_type || invoiceType) === "B2C";
        const payload = {
            name: l.name,
            hsn: l.hsn || "",
            mrp: 0,
            b2b_price: isB2C ? 0 : Number(l.rate),
            b2c_price: isB2C ? Number(l.rate) : 0,
            our_price: Number(l.rate),
            tax_percent: Number(l.tax_percent) || 0,
            category: l.category || "Uncategorized",
            unit: l.unit || "Pack",
            active: true,
        };
        try {
            const r = await api.post("/items", payload);
            setDraft((cur) => ({
                ...cur,
                line_items: cur.line_items.map((row, k) => k === i ? {
                    ...row,
                    item_id: r.data.id,
                    hsn: r.data.hsn,
                    match_status: "matched",
                    match_score: 100,
                } : row),
                stats: {
                    ...cur.stats,
                    matched: cur.stats.matched + 1,
                    unmatched: Math.max(0, cur.stats.unmatched - 1),
                },
            }));
            toast.success(`"${r.data.name}" added to Item Master (${isB2C ? "B2C" : "B2B"} rate)`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save item");
        }
    };

    const saveAllUnmatched = async () => {
        const readyIdx = draft.line_items
            .map((l, i) => ({ l, i }))
            .filter(({ l }) => l.match_status !== "matched" && l.name && Number(l.rate) > 0);
        if (readyIdx.length === 0) return toast.error("No unmatched rows have a rate filled in yet");
        if (!confirm(`Add ${readyIdx.length} new item${readyIdx.length > 1 ? "s" : ""} to your Item Master?`)) return;
        for (const { i } of readyIdx) {
            // eslint-disable-next-line no-await-in-loop
            await promoteToMaster(i);
        }
    };

    const totals = useMemo(() => {
        if (!draft) return { sub: 0, tax: 0, gt: 0 };
        let sub = 0, tax = 0;
        for (const l of draft.line_items) {
            const t = (Number(l.qty) || 0) * (Number(l.rate) || 0);
            const g = t * (Number(l.tax_percent) || 0) / 100;
            sub += t; tax += g;
        }
        return { sub, tax, gt: sub + tax + Number(delivery || 0) };
    }, [draft, delivery]);

    const save = async () => {
        const withRates = draft.line_items.filter((l) => l.name && l.qty > 0 && Number(l.rate) > 0);
        if (withRates.length === 0) return toast.error("Add rates to at least one line item before saving");
        const skipped = draft.line_items.length - withRates.length;
        if (skipped > 0) {
            if (!confirm(`${skipped} item(s) have blank rate — they will be excluded from the invoice. Save anyway?`)) return;
        }
        setSaving(true);
        try {
            const r = await api.post("/invoices", {
                buyer: draft.buyer,
                line_items: withRates.map((l) => ({
                    item_id: l.item_id || null,
                    name: l.name, hsn: l.hsn, category: l.category, unit: l.unit,
                    qty: Number(l.qty), rate: Number(l.rate), tax_percent: Number(l.tax_percent),
                })),
                delivery: Number(delivery || 0),
                invoice_type: draft.invoice_type || invoiceType,
                notes: draft.po_number ? `Auto-generated from PO ${draft.po_number}` : "",
            });
            toast.success(`Invoice ${r.data.invoice_number} created from PO`);
            nav(`/invoices/${r.data.id}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        setSaving(false);
    };

    // ---------------- UPLOAD SCREEN ----------------
    if (step === "upload") {
        return (
            <div data-testid="po-upload" className="max-w-3xl mx-auto pt-4">
                <div className="mb-6">
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">Import Purchase Order</h1>
                    <p className="text-slate-500 mt-1 text-sm">
                        Upload a client's PO PDF — HDB will auto-generate a draft invoice with matched items pre-priced from your Item Master, and flag any new items for you to fill.
                    </p>
                </div>

                <div className="hdb-card p-6 mb-4">
                    <label className="hdb-label mb-3">Pricing bracket for this PO</label>
                    <div className="flex gap-2 mb-6">
                        {["B2B", "B2C"].map((t) => (
                            <button key={t} onClick={() => setInvoiceType(t)}
                                    data-testid={`po-type-${t}`}
                                    className={`px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${
                                        invoiceType === t
                                            ? "bg-slate-900 text-white border-slate-900"
                                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-900"
                                    }`}>
                                {t}
                            </button>
                        ))}
                    </div>

                    <label className="hdb-label">Purchase Order PDF</label>
                    <label className={`block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                        file ? "border-emerald-400 bg-emerald-50/40" : "border-slate-300 hover:border-slate-900 bg-slate-50/60"
                    }`}>
                        <Upload size={22} className="mx-auto text-slate-500 mb-2" />
                        <div className="text-sm text-slate-700 font-semibold">
                            {file ? file.name : "Drop or select a PO PDF"}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            Native/typed PDFs work best. Scanned images need OCR (not yet supported).
                        </div>
                        <input type="file" accept="application/pdf,.pdf"
                               className="hidden"
                               onChange={(e) => setFile(e.target.files?.[0] || null)}
                               data-testid="po-file" />
                    </label>

                    <div className="mt-5 flex items-center justify-between">
                        <button onClick={() => nav("/invoices/new")} className="hdb-btn hdb-btn-ghost">
                            <ArrowLeft size={14} /> Manual entry instead
                        </button>
                        <button onClick={parse} disabled={!file || parsing}
                                className="hdb-btn hdb-btn-primary" data-testid="po-parse-btn">
                            <Sparkles size={14} /> {parsing ? "Reading PO…" : "Parse & Generate Draft"}
                        </button>
                    </div>
                </div>

                <div className="hdb-card p-5 bg-slate-50/60">
                    <div className="hdb-heading font-bold text-sm text-slate-900 mb-2">How it works</div>
                    <ol className="text-xs text-slate-600 space-y-1.5 list-decimal pl-4">
                        <li>Text is extracted from the PDF (typed PDFs give best results).</li>
                        <li>Gemini 3 Flash parses buyer info + every line item with quantity and unit.</li>
                        <li>Each item is fuzzy-matched against your Item Master to auto-fill rate, HSN, GST%.</li>
                        <li>Unmatched items appear with a warning badge so you can add rate before saving.</li>
                    </ol>
                </div>
            </div>
        );
    }

    // ---------------- REVIEW SCREEN ----------------
    const d = draft;
    return (
        <div data-testid="po-review">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                        {d.po_number ? `From PO ${d.po_number}` : "Draft invoice"}
                    </div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">Review & Save</h1>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setStep("upload")} className="hdb-btn hdb-btn-ghost" data-testid="po-back-btn">
                        <ArrowLeft size={14} /> Upload different PO
                    </button>
                    <button onClick={save} disabled={saving} className="hdb-btn hdb-btn-mint" data-testid="po-save-btn">
                        <Save size={14} /> {saving ? "Saving…" : "Save Invoice"}
                    </button>
                </div>
            </div>

            {/* Stats banner */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                <div className="hdb-card p-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded bg-emerald-100 text-emerald-700 flex items-center justify-center">
                        <CheckCircle2 size={18} />
                    </div>
                    <div>
                        <div className="hdb-num text-2xl font-black text-slate-900">{d.stats.matched}</div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Matched & priced</div>
                    </div>
                </div>
                <div className="hdb-card p-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded bg-amber-100 text-amber-700 flex items-center justify-center">
                        <AlertCircle size={18} />
                    </div>
                    <div>
                        <div className="hdb-num text-2xl font-black text-slate-900">{d.stats.unmatched}</div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Need pricing</div>
                    </div>
                </div>
                <div className="hdb-card p-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded bg-slate-900 text-white flex items-center justify-center">
                        <Sparkles size={18} />
                    </div>
                    <div>
                        <div className="hdb-num text-2xl font-black text-slate-900">{d.line_items.length}</div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Total line items</div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-5">
                    {/* Buyer */}
                    <div className="hdb-card p-5">
                        <h3 className="hdb-heading font-bold text-lg text-slate-900 mb-4">Bill To</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <BField label="Buyer Name *" v={d.buyer.name} onChange={(v) => setDraft({...d, buyer:{...d.buyer, name:v}})} testid="po-buyer-name" full />
                            <BField label="Address" v={d.buyer.address} onChange={(v) => setDraft({...d, buyer:{...d.buyer, address:v}})} full />
                            <BField label="GSTIN" v={d.buyer.gstin} onChange={(v) => setDraft({...d, buyer:{...d.buyer, gstin:v.toUpperCase()}})} mono />
                            <BField label="Contact" v={d.buyer.contact} onChange={(v) => setDraft({...d, buyer:{...d.buyer, contact:v}})} />
                            <BField label="State" v={d.buyer.state} onChange={(v) => setDraft({...d, buyer:{...d.buyer, state:v}})} />
                            <BField label="State Code" v={d.buyer.state_code} onChange={(v) => setDraft({...d, buyer:{...d.buyer, state_code:v}})} mono />
                        </div>
                    </div>

                    {/* Line items */}
                    <div className="hdb-card overflow-visible">
                        <div className="flex justify-between items-center p-5 border-b border-slate-100">
                            <h3 className="hdb-heading font-bold text-lg text-slate-900">Line Items</h3>
                            <div className="flex gap-2">
                                {d.stats.unmatched > 0 && (
                                    <button className="hdb-btn hdb-btn-ghost" onClick={saveAllUnmatched}
                                            data-testid="save-all-new-items-btn">
                                        <PackagePlus size={13} /> Save all filled items to Master
                                    </button>
                                )}
                                <button className="hdb-btn hdb-btn-ghost" onClick={addLine} data-testid="add-po-line">
                                    <Plus size={13} /> Add row
                                </button>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="hdb-table w-full min-w-[1000px]">
                                <thead>
                                    <tr>
                                        <th style={{width: 24}}></th>
                                        <th>Item</th>
                                        <th>Category</th>
                                        <th>HSN</th>
                                        <th style={{width: 68}}>Qty</th>
                                        <th style={{width: 68}}>Unit</th>
                                        <th className="text-right" style={{width: 100}}>Rate</th>
                                        <th className="text-right" style={{width: 68}}>GST%</th>
                                        <th className="text-right" style={{width: 100}}>Total</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {d.line_items.map((l, i) => {
                                        const total = (Number(l.qty)||0) * (Number(l.rate)||0) * (1 + (Number(l.tax_percent)||0)/100);
                                        const need = l.match_status !== "matched";
                                        return (
                                            <tr key={i} className={need ? "bg-amber-50/40" : ""} data-testid={`po-line-${i}`}>
                                                <td className="text-center">
                                                    {need
                                                        ? <AlertCircle size={14} className="text-amber-500" title="Not in item master" />
                                                        : <CheckCircle2 size={14} className="text-emerald-500" title={`Matched (${l.match_score}%)`} />}
                                                </td>
                                                <td>
                                                    <input className="hdb-input" value={l.name}
                                                           onChange={(e) => setLine(i, {name: e.target.value})} />
                                                    {l.raw_po_name && l.raw_po_name !== l.name && (
                                                        <div className="text-[10px] text-slate-400 mt-0.5">PO: {l.raw_po_name}</div>
                                                    )}
                                                </td>
                                                <td>
                                                    <input className="hdb-input" value={l.category}
                                                           onChange={(e) => setLine(i, {category: e.target.value})} />
                                                </td>
                                                <td><input className="hdb-input hdb-mono" value={l.hsn}
                                                           onChange={(e) => setLine(i, {hsn: e.target.value})} /></td>
                                                <td><input className="hdb-input hdb-mono" type="number" value={l.qty}
                                                           onChange={(e) => setLine(i, {qty: e.target.value})} /></td>
                                                <td><input className="hdb-input" value={l.unit}
                                                           onChange={(e) => setLine(i, {unit: e.target.value})} /></td>
                                                <td><input className={`hdb-input hdb-mono text-right ${need && (!l.rate || Number(l.rate)===0) ? "border-amber-400 bg-amber-50" : ""}`}
                                                           type="number" value={l.rate}
                                                           onChange={(e) => setLine(i, {rate: e.target.value})}
                                                           data-testid={`po-line-rate-${i}`}
                                                           placeholder="Fill rate" /></td>
                                                <td><input className="hdb-input hdb-mono text-right" type="number" value={l.tax_percent}
                                                           onChange={(e) => setLine(i, {tax_percent: e.target.value})} /></td>
                                                <td className="hdb-num hdb-num-right font-semibold">{fmtNum(total)}</td>
                                                <td className="text-right whitespace-nowrap">
                                                    {need && Number(l.rate) > 0 && l.name && (
                                                        <button className="hdb-btn hdb-btn-mint mr-1 !px-2 !py-1"
                                                                onClick={() => promoteToMaster(i)}
                                                                data-testid={`save-to-master-${i}`}
                                                                title="Add this to Item Master">
                                                            <PackagePlus size={12} />
                                                        </button>
                                                    )}
                                                    <button className="hdb-btn hdb-btn-danger" onClick={() => rmLine(i)}>
                                                        <Trash2 size={12} />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Totals */}
                <div>
                    <div className="hdb-card p-5 sticky top-4">
                        <h3 className="hdb-heading font-bold text-lg text-slate-900 mb-4">Summary</h3>
                        <div className="mb-3">
                            <label className="hdb-label">Delivery (₹)</label>
                            <input className="hdb-input hdb-mono" type="number" value={delivery}
                                   onChange={(e) => setDelivery(e.target.value)} />
                        </div>
                        <div className="space-y-2 text-sm">
                            <RowKV k="Subtotal" v={fmt(totals.sub)} />
                            <RowKV k="Total tax" v={fmt(totals.tax)} />
                            {Number(delivery) > 0 && <RowKV k="Delivery" v={fmt(Number(delivery))} />}
                            <div className="border-t border-slate-200 pt-3 mt-3 flex justify-between">
                                <div className="hdb-heading font-bold text-slate-900">Grand Total</div>
                                <div className="hdb-heading font-black text-slate-900 text-xl hdb-num" data-testid="po-grand-total">
                                    {fmt(totals.gt)}
                                </div>
                            </div>
                        </div>
                        {d.stats.unmatched > 0 && (
                            <div className="mt-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                                <b>{d.stats.unmatched}</b> item{d.stats.unmatched>1?"s":""} still need pricing. Blank-rate rows will be excluded when saving.
                                <div className="mt-1.5 text-[11px] text-amber-700">
                                    💡 After filling a rate, click <b>Save to Master</b> on that row (or <b>Save all filled items</b> above) — HDB will add it to your Item Master so you never enter it again.
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function BField({ label, v, onChange, mono, full, testid }) {
    return (
        <div className={full ? "col-span-2" : ""}>
            <label className="hdb-label">{label}</label>
            <input className={`hdb-input ${mono ? "hdb-mono" : ""}`} value={v || ""}
                   onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        </div>
    );
}
function RowKV({ k, v }) {
    return <div className="flex justify-between text-slate-700"><span>{k}</span><span className="hdb-num font-semibold">{v}</span></div>;
}
